// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/** English UI strings: Account settings and other pages (routes/*, components/*). Merged into the catalog by messages.en.ts. */
export const settings = {
  // routes/SettingsPage.tsx
  "settings.title": "Settings",
  "settings.docTitle": "Settings · {app}",
  "settings.group.workspaces": "Workspaces",
  "settings.sectionsNav": "Settings sections",
  "settings.section.profile": "Profile",
  "settings.section.security": "Security",
  "settings.section.devices": "Devices",
  "settings.section.notifications": "Notifications",
  "settings.section.apps": "Connected apps",
  "settings.section.ai": "AI tools",
  "settings.section.app": "App",
  "about.title": "About Granite",
  "about.tagline": "Markdown notes that sync.",
  "about.version": "Version {version} ({build})",
  "about.checkUpdates": "Check for updates",
  "about.checking": "Checking for updates…",
  "about.upToDate": "Granite is up to date.",
  "about.checkAgain": "Check again",
  "about.available": "Granite {version} is available.",
  "about.install": "Install and restart",
  "about.installing": "Installing the update…",
  "about.failed": "Could not check for updates: {error}",
  "about.links": "Granite on the web",
  "about.website": "Website",
  "about.help": "Help",
  "about.whatsNew": "What's new",
  "about.security": "Security",
  "about.privacy": "Privacy",
  "about.terms": "Terms",
  "about.copyDetails": "Copy system details",
  "about.copied": "Copied",
  "about.copyright": "© {year} Granite. All rights reserved.",
  "settings.app.trayIcon": "Show Granite in the menu bar",
  "settings.app.trayIconHint": "An icon to open Granite or start a note. In the notification area on Windows.",
  "settings.app.keepRunning": "Keep running when the window is closed",
  "settings.app.keepRunningHint": "Closing the window hides it; quit from the app menu or the menu bar icon. Off: closing quits.",
  "settings.app.openAtLogin": "Open at login",
  "settings.app.openAtLoginHint": "Start Granite when you log in to this computer.",
  "settings.app.autoUpdate": "Install updates automatically",
  "settings.app.autoUpdateHint": "Check for new versions every day and offer to install them.",
  "settings.app.checkNow": "Check for updates now",
  "settings.section.appearance": "Appearance",
  "settings.section.hotkeys": "Hotkeys",
  "settings.section.data": "Your data",
  "settings.group.account": "Account",
  "settings.group.connections": "Connections",
  "settings.group.preferences": "Preferences",

  "settings.profile.description": "How you appear to people you share notes with.",
  "settings.profile.saved": "Saved.",
  "settings.profile.email": "Email",
  "settings.profile.photo": "Photo",
  "settings.profile.photoHint": "Shown next to your name. Cropped to a square.",
  "settings.profile.photoChoose": "Choose a photo",
  "settings.profile.photoUpload": "Upload photo",
  "settings.profile.photoChange": "Change",
  "settings.profile.photoRemove": "Remove",
  "settings.profile.emailHint": "Contact support to change your email address.",
  "settings.profile.name": "Name",
  "settings.profile.save": "Save",

  "settings.stepUp.intro": "Confirm it is you to continue.",
  "settings.stepUp.currentPassword": "Current password",
  "settings.stepUp.code": "Authentication code",
  "settings.stepUp.confirm": "Confirm",

  "settings.password.title": "Password",
  "settings.password.description": "Changing your password signs out your other devices.",
  "settings.password.changed": "Password changed. Other devices were signed out.",
  "settings.password.new": "New password",
  "settings.password.newHint": "At least 10 characters, and hard to guess.",
  "settings.password.repeat": "Repeat the new password",
  "settings.password.mismatch": "The passwords do not match.",
  "settings.password.submit": "Change password",

  "settings.twoFactor.title": "Two-step verification",
  "settings.twoFactor.description": "Ask for a code from an authenticator app when signing in.",
  "settings.twoFactor.saveCodes": "Save these recovery codes somewhere safe. Each works once, and they are shown only now.",
  "settings.twoFactor.recoveryCodes": "Recovery codes",
  "settings.twoFactor.scan": "Scan this with your authenticator app, then enter the 6-digit code it shows.",
  "settings.twoFactor.qrCode": "QR code for your authenticator app",
  "settings.twoFactor.manualKey": "Or enter this key by hand:",
  "settings.twoFactor.setupKey": "Setup key",
  "settings.twoFactor.codeFromApp": "Code from the app",
  "settings.twoFactor.turnOn": "Turn on",
  "settings.twoFactor.cancel": "Cancel",
  "settings.twoFactor.isOn": "Two-step verification is on.",
  "settings.twoFactor.newCodes": "New recovery codes",
  "settings.twoFactor.turnOff": "Turn off",
  "settings.twoFactor.setUp": "Set up two-step verification",

  "settings.platform.web": "Web browser",
  "settings.platform.macos": "Mac",
  "settings.platform.windows": "Windows",
  "settings.platform.ios": "iPhone or iPad",
  "settings.platform.android": "Android",
  "settings.platform.cli": "Command line",
  "settings.platform.other": "Other",

  "settings.devices.description":
    "Devices signed in to your account. Signing a device out ends its session within a minute; your plan limits how many devices sync at once.",
  "settings.devices.list": "Devices",
  "settings.devices.thisDevice": "This device",
  "settings.devices.meta": "{platform} · last used {when}",
  "settings.devices.metaWithVersion": "{platform} · {version} · last used {when}",
  "settings.devices.signOutDevice": "Sign out {name}",
  "settings.devices.signOut": "Sign out",
  "settings.devices.signOutOthers": "Sign out all other devices",

  "settings.notifications.description":
    "Choose which notifications also reach you by email. They always appear in Granite. Being added to a team is always emailed.",
  "settings.notifications.descriptionDesktop":
    "Choose which notifications also reach you by email and as notifications on this computer. They always appear in Granite. Being added to a team is always emailed and shown.",
  "settings.notifications.email": "Email",
  "settings.notifications.desktop": "Desktop",
  "settings.notifications.emailFor": "Email for {kind}",
  "settings.notifications.desktopFor": "Desktop notifications for {kind}",
  "settings.notifications.comment": "Comments",
  "settings.notifications.commentHint": "Replies in threads you wrote in.",
  "settings.notifications.share": "Sharing",
  "settings.notifications.shareHint": "A vault, folder, or note is shared with you.",
  "settings.notifications.mention": "Mentions",
  "settings.notifications.mentionHint": "Someone mentions you in a comment.",
  "settings.notifications.resolved": "Resolved comments",
  "settings.notifications.resolvedHint": "A thread on a note you created or edited is resolved.",

  "settings.appearance.description":
    "Theme, font, text size, line width, and live preview follow you to every device you sign in on. Signed out, they apply to this device only.",
  "settings.appearance.theme": "Theme",
  "settings.appearance.themeSystem": "Same as the system",
  "settings.appearance.themeLight": "Light",
  "settings.appearance.themeDark": "Dark",
  "settings.appearance.textSize": "Text size: {size} px",
  "settings.appearance.fullWidth": "Full width",
  "settings.appearance.fullWidthHint": "Notes use the whole width of the window, on all your devices.",
  "settings.appearance.font": "Note font",
  "settings.appearance.fontSans": "Default",
  "settings.appearance.fontMono": "Monospace",
  "settings.appearance.livePreview": "Live preview while editing",
  "settings.appearance.vim": "Vim key bindings",
  "settings.appearance.lineNumbers": "Line numbers",
  "settings.appearance.thisDeviceOnly": "Only on this device.",

  "settings.hotkeys.description": "Shortcuts for commands on this device. Every command is also in the command palette.",
  "settings.hotkeys.list": "Hotkeys",
  "settings.hotkeys.recording": "Press keys… (Esc cancels)",
  "settings.hotkeys.setFor": "Set shortcut for {command}",
  "settings.hotkeys.set": "Set",
  "settings.hotkeys.resetFor": "Reset shortcut for {command}",
  "settings.hotkeys.reset": "Reset",
  "settings.hotkeys.inEditor": "In the editor",
  "settings.hotkeys.editorShortcuts": "Editor shortcuts",
  "settings.hotkeys.editor.bold": "Bold",
  "settings.hotkeys.editor.italic": "Italic",
  "settings.hotkeys.editor.inlineCode": "Inline code",
  "settings.hotkeys.editor.strikethrough": "Strikethrough",
  "settings.hotkeys.editor.highlight": "Highlight",
  "settings.hotkeys.editor.link": "Link to a note",
  "settings.hotkeys.editor.task": "Task: add or check",
  "settings.hotkeys.editor.find": "Find in note",
  "settings.hotkeys.editor.undoRedo": "Undo / redo",

  "settings.tokenScope.vaultsRead": "Read notes",
  "settings.tokenScope.vaultsWrite": "Read and change everything (notes, folders, templates)",
  "settings.tokenScope.notesCreate": "Create notes",
  "settings.tokenScope.notesEdit": "Edit, move, and delete notes",
  "settings.tokenScope.foldersCreate": "Create folders",
  "settings.tokenScope.foldersEdit": "Rename, move, and delete folders",
  "settings.tokenScope.templatesEdit": "Add, edit, and delete templates",
  "settings.tokenScope.sync": "Sync folders (granite sync)",
  "settings.tokenScope.kbQuery": "Search the knowledge base",
  "settings.never": "never",

  "settings.apps.title": "Connected apps",
  "settings.apps.description": "AI tools and other apps you allowed to use your vaults. Disconnecting one stops it at once.",
  "settings.apps.none": "No apps are connected.",
  "settings.apps.list": "Connected apps",
  "settings.apps.allVaults": "all vaults",
  "settings.apps.vaultCount": "{count} vault(s)",
  "settings.apps.meta": "{scopes} · {vaults} · last used {when}",
  "settings.apps.disconnect": "Disconnect",

  "settings.tokens.title": "API tokens",
  "settings.tokens.description": "For scripts, bots, and granite sync on servers. A token is shown once; keep it secret.",
  "settings.tokens.copyNow": "Copy this token now; it will not be shown again:",
  "settings.tokens.list": "API tokens",
  "settings.tokens.meta": "{scopes} · last used {when}",
  "settings.tokens.revoke": "Revoke",
  "settings.tokens.name": "Token name",
  "settings.tokens.permissions": "Permissions",
  "settings.tokens.create": "Create token",

  "settings.ai.intro": "AI assistants can read, search, and (if you allow it) edit your notes over MCP.",
  "settings.ai.guide": "Setup and security guide",
  "settings.ai.localInDesktop":
    "The local MCP server comes with the Granite desktop app: it works on the vault folders on your computer, offline and without the cloud. Open this page in the desktop app to set it up.",
  "settings.ai.getDesktop": "Download Granite for desktop",
  "settings.ai.localTitle": "Local vaults",
  "settings.ai.localDescription":
    "Granite runs an MCP server on this computer for your vault folders: AI tools connect to its address with the token. Offline, no account needed. Paste a snippet into the tool's settings.",
  "settings.ai.server": "Run the local MCP server",
  "settings.ai.serverOff": "Off: AI tools cannot reach your vault folders through Granite.",
  "settings.ai.serverRunning": "Running at {address}, on this computer only.",
  "settings.ai.serverStarting": "Starting…",
  "settings.ai.port": "Port",
  "settings.ai.savePort": "Use this port",
  "settings.ai.token": "Token",
  "settings.ai.tokenHint": "AI tools send it with every request; nothing else on this computer can use the server without it.",
  "settings.ai.show": "Show",
  "settings.ai.hide": "Hide",
  "settings.ai.newToken": "New token",
  "settings.ai.newTokenConfirm": "Make a new token? AI tools set up with the old one stop working until you paste the new one.",
  "settings.ai.noVaults": "No vaults yet: each folder in Documents › Granite is one, and the server shares them all.",
  "settings.ai.chatgptLocal": "ChatGPT connects from OpenAI's servers, so it cannot reach this computer: sign in and use the Granite cloud server below.",
  "settings.ai.address": "Address",
  "settings.ai.everyVault": "every vault, one connection",
  "settings.ai.sharesVaults": "One connection for every shared vault: {names}. AI tools list them and pick one.",
  "settings.ai.vaultsTitle": "Vaults",
  "settings.ai.vaultsHint": "Each shared vault also has its own port: an AI tool set up with its address sees that vault and no other.",
  "settings.ai.vaultPort": "This vault's port",
  "settings.ai.vaultPortHint": "Every vault has its own port, so AI tools set up for one vault never see another. Change it if another program needs this one.",
  "settings.ai.shareThisVault": "Share {name} with AI tools",
  "settings.ai.onlyThisVault": "this vault only",
  "settings.ai.vaultOff": "Off: AI tools cannot reach this vault through Granite. Turning it on starts the local MCP server.",
  "settings.ai.cloudVault": "AI tools reach this vault through Granite's MCP server, signed in to your account. Ask for {name} by name, or turn on its own folder in the desktop app.",
  "settings.ai.allSettings": "All MCP settings",
  "settings.ai.startsItself": "starts Granite's server itself",
  "settings.ai.vault": "Vault",
  "settings.ai.readOnly": "Read only",
  "settings.ai.syncedTitle": "Synced vaults",
  "settings.ai.syncedDescription":
    "AI tools anywhere can use your synced vaults through Granite's MCP server. You approve each tool and choose what it may do; manage them under Connected apps.",
  "settings.ai.copied": "Copied",
  "settings.ai.copy": "Copy",

  "settings.data.downloadTitle": "Download your data",
  "settings.data.downloadDescription": "A zip with every note and file in your vaults, as plain files, and your account details.",
  "settings.data.downloadAll": "Download all my data",
  "settings.data.downloadJson": "Download all my data (JSON)",
  "settings.data.downloadUpgrade":
    "On Free, everything comes as one JSON file, with your notes and files inside it. Pro and Business download a zip of ordinary files and folders.",
  "settings.data.seePlans": "See plans",
  "settings.data.deleteCompany":
    "Your account belongs to {workspace}. Its owner or an admin can delete it, and can give your folders and notes to a colleague or download them first.",
  "settings.data.deleteTitle": "Delete account",
  "settings.data.deleteDescription":
    "Deletes your account and your personal vaults for good, and signs you out everywhere. Backups are gone within 35 days.",
  "settings.data.deleteDescriptionOwner":
    "Deletes your account and your personal vaults for good, and signs you out everywhere. Workspaces you own alone are deleted too; transfer shared ones first. Backups are gone within 35 days.",
  "settings.data.deleteForm": "Delete account",
  "settings.data.deletePhrase": "delete my account",
  "settings.data.typeToConfirm": "Type “{phrase}” to confirm",
  "settings.data.deleteSubmit": "Delete my account",

  // routes/OAuthConsentPage.tsx
  "oauth.scope.vaultsRead": "Read your notes",
  "oauth.scope.vaultsWrite": "Create, edit, move, and delete notes",
  "oauth.scope.kbQuery": "Search your knowledge base",
  "oauth.cannotConnect": "Can't connect this app",
  "oauth.tryAgain": "Go back to the app and try connecting again.",
  "oauth.connectTitle": "Connect an app",
  "oauth.loading": "Loading",
  "oauth.allowTitle": "Allow {app}?",
  "oauth.wantsToUse": "wants to use your Granite notes. It will send you back to",
  "oauth.itMay": "It may",
  "oauth.in": "In",
  "oauth.allVaults": "All your vaults",
  "oauth.onlyTheseVaults": "Only these vaults",
  "oauth.vaultsList": "Vaults the app may use",
  "oauth.allow": "Allow",
  "oauth.deny": "Deny",
  "oauth.disconnectHint": "You can disconnect the app any time in Settings → Connected apps.",

  // routes/LocalVaultsPage.tsx
  "localVaults.desktopOnly": "Folders on your computer can be opened in the Granite desktop app.",
  "localVaults.docTitle": "Vaults on this computer · {app}",
  "localVaults.synced": "Synced vaults",
  "localVaults.signInToSync": "Sign in to sync",
  "localVaults.aiTools": "AI tools",
  "localVaults.title": "Vaults on this computer",
  "localVaults.intro": "A vault is a folder of Markdown files. Granite works on it directly, offline, and other apps can edit it too.",
  "localVaults.list": "Local vaults",
  "localVaults.syncsWith": "{path} · syncs with “{vault}”",
  "localVaults.aServerVault": "a server vault",
  "localVaults.localOnly": "{path} · on this computer only",
  "localVaults.syncSettings": "Sync settings",
  "localVaults.sync": "Sync…",
  "localVaults.removeFromList": "Remove {name} from the list",
  "localVaults.remove": "Remove",
  "localVaults.syncWith": "Sync this folder with",
  "localVaults.newServerVault": "a new server vault named “{name}”",
  "localVaults.startSyncing": "Start syncing",
  "localVaults.open": "Open a folder as a vault…",
  "localVaults.openElsewhere": "Open a folder elsewhere…",
  "localVaults.newTitle": "New vault",
  "localVaults.newName": "New vault",
  "localVaults.create": "Create vault",
  "localVaults.where": "Each vault is a folder in {path}, with its own folders inside. A folder you add there is a vault too.",
  "localVaults.reveal": "Show in Finder",
  "localVaults.revealWindows": "Show in Explorer",
  "localVaults.removeHint": "Vaults opened from elsewhere can be removed from this list; their folders and files stay untouched.",

  // routes/InvitePage.tsx
  "invite.title": "Invitation",
  "invite.loading": "Loading",
  "invite.joinTitle": "Join {workspace}",
  "invite.invitedTo": "You are invited to the workspace",
  "invite.asRole": "as a",
  "invite.forEmail": "(for",
  "invite.accept": "Accept invitation",
  "invite.or": "or",
  "invite.createAccount": "create an account",
  "invite.toAccept": "to accept.",

  // routes/HomePage.tsx
  "home.settings": "Settings",
  "shell.sidebar": "Sidebar",
  "shell.mainNav": "Main",
  "shell.home": "Home",
  "shell.settings": "Settings",
  "shell.vaults": "Vaults",
  "shell.personal": "Personal",
  "shell.workspaces": "Workspaces",
  "shell.noVaults": "No vaults yet",
  "shell.sharedWithMe": "Shared with me",
  "shell.helpTitle": "Help center (opens in a new tab)",
  "shell.account": "Account menu for {email}",
  "shell.theme": "Theme",
  "shell.signOut": "Sign out",
  "shell.newVault": "New vault",
  "shell.workspaceAdd": "Add to {name}",
  "shell.addMember": "Add member",
  "shell.addVault": "Add vault",
  "shell.search": "Search",
  "shell.hideSidebar": "Hide sidebar",
  "shell.resizeSidebar": "Resize sidebar",
  "shell.showSidebar": "Show sidebar",
  "shell.openMenu": "Open menu",
  "home.vaultsHeading": "Vaults",
  "home.vaultHint": "Plain Markdown notes, synced and encrypted.",
  "home.workspacesHint": "Shared vaults, members, SSO, and admin controls for your company.",
  "theme.switchToLight": "Switch to light mode",
  "theme.switchToDark": "Switch to dark mode",
  "home.workspaces": "Workspaces",
  "home.yourWorkspaces": "Your workspaces",

  // routes/SSOPage.tsx, routes/LoginPage.tsx
  "sso.title": "Sign in with SSO",
  "sso.usePassword": "Sign in with a password instead",
  "sso.workEmail": "Work email",
  "sso.continue": "Continue",
  "sso.signingIn": "Signing you in",
  "sso.idpFailed": "The identity provider did not sign you in.",
  "sso.idpFailedWithReason": "The identity provider did not sign you in: {reason}",
  "sso.incompleteLink": "This sign-in link is incomplete.",
  "sso.tryAgain": "Try again",
  "sso.toApp": "Back to the Granite app to finish signing in…",
  "login.withoutAccount": "Use Granite without an account",

  // components/NotificationsBell.tsx
  "notifications.title": "Notifications",
  "notifications.unread": "Notifications, {count} unread",
  "notifications.newCount": "{count} new notifications",
  "notifications.nothingNew": "Nothing new.",
  "notifications.someone": "Someone",
  "notifications.mentionedYouIn": "mentioned you in",
  "notifications.repliedIn": "replied in",
  "notifications.resolvedIn": "resolved a comment in",
  "notifications.addedYouToTeam": "added you to the team",
  "notifications.sharedVault": "shared the vault",
  "notifications.sharedFolder": "shared the folder",
  "notifications.sharedNote": "shared the note",
  "notifications.markAllRead": "Mark all read",
} as const;
